import { divideArrayIntoChunks } from '@pkg/basic'
import { eq } from 'drizzle-orm'
import {
  accountApiLostColumnCount,
  accountApiPendingColumnCount,
} from '../../constants'
import {
  accountApiLostTable,
  accountApiPendingTable,
  accountApiTable,
} from '../../schema'
import type { Database, DatabaseWithTransaction } from '../../types'
import { getMaxInsertRowCount } from '../../utils'

type UpdateApiSyncStatusParameters = {
  apiId: number
  database: DatabaseWithTransaction
  /** 現在から遡って取得可能な最大期間 (UNIX milliseconds) */
  fetchableRange?: number
  /** 同期開始時間 */
  startTime: number
}

export const updateApiSyncStatus = async ({
  apiId,
  database: { database, transaction },
  fetchableRange,
  startTime,
}: UpdateApiSyncStatusParameters) => {
  // DB から accountApi , accountApiPending, accountApiLost を全取得
  const apiData = await getApiData({ apiId, database })
  if (!apiData) {
    throw new Error(`API with ID ${apiId} not found`)
  }
  const { api, losts, pendings } = apiData

  // pending / lost を最新の状態に更新
  // - 現時刻から最終同期時間（未定義の場合は startTime）までを Pending に追加
  // - 現時刻 - fetchableRange 以前の Pending を Lost に変更
  // - 連続する Pending / Lost の期間を merge する
  const now = Date.now()
  const lastSyncedAt = api.to?.getTime() ?? startTime - 1
  const { losts: newLosts, pendings: newPendings } = updateApiRanges({
    fetchableRange,
    lastSyncedAt,
    now,
    losts: losts.map((lost) => ({
      from: lost.from.getTime(),
      to: lost.to.getTime(),
    })),
    pendings: pendings.map((pending) => ({
      from: pending.from.getTime(),
      to: pending.to.getTime(),
    })),
  })

  // DB に保存
  // - accountApi の from, to を更新
  // - accountApiPending のデータを全削除して、newPendings を追加
  // - accountApiLost のデータを全削除して、newLosts を追加
  await saveData({
    apiId,
    from: api.from?.getTime() ?? startTime,
    losts: newLosts,
    pendings: newPendings,
    to: now,
    transaction,
  })

  // 保存後の pending data を取得して返す
  const pendingRows = await database
    .select()
    .from(accountApiPendingTable)
    .where(eq(accountApiPendingTable.apiId, apiId))
  return pendingRows.map((row) => ({
    id: row.id,
    apiId: row.apiId,
    from: row.from.getTime(),
    to: row.to.getTime(),
  }))
}

const getApiData = async (params: {
  apiId: number
  database: Database
}) => {
  const { apiId, database } = params

  const apiRows = await database
    .select()
    .from(accountApiTable)
    .where(eq(accountApiTable.id, apiId))
    .limit(1)
  const lostRows = await database
    .select()
    .from(accountApiLostTable)
    .where(eq(accountApiLostTable.apiId, apiId))
  const pendingRows = await database
    .select()
    .from(accountApiPendingTable)
    .where(eq(accountApiPendingTable.apiId, apiId))

  if (apiRows.length === 0) {
    return undefined
  }

  return {
    api: apiRows[0],
    losts: lostRows.sort((a, b) => a.from.getTime() - b.from.getTime()),
    pendings: pendingRows.sort((a, b) => a.from.getTime() - b.from.getTime()),
  }
}

type RangeData = {
  from: number
  to: number
}

const updateApiRanges = (params: {
  fetchableRange?: number
  lastSyncedAt: number
  now: number
  losts: RangeData[]
  pendings: RangeData[]
}) => {
  const { fetchableRange, lastSyncedAt, now, losts, pendings } = params

  // lastSyncedAt から now までの期間を Pending に追加
  // ただし lastSyncedAt が 1 時間以上前の場合に限定
  if (lastSyncedAt < now - 60 * 60 * 1000) {
    pendings.push({
      from: lastSyncedAt + 1,
      to: now,
    })
  }

  // fetchableRange が定義されている場合、fetchableRange 以前の pending 期間を Lost に変換
  const removedPendings: RangeData[] = []
  if (fetchableRange) {
    const fetchableFrom = now - fetchableRange
    for (const pending of pendings) {
      if (pending.to < fetchableFrom) {
        // to が fetchableFrom より前の場合、 losts に追加
        losts.push({
          from: pending.from,
          to: pending.to,
        })
      } else if (pending.from < fetchableFrom) {
        // from が fetchableFrom より前の場合、 from ~ fetchableFrom - 1 を losts に / fetchableFrom ~ to を pendings に追加
        losts.push({
          from: pending.from,
          to: fetchableFrom - 1,
        })
        removedPendings.push({
          from: fetchableFrom,
          to: pending.to,
        })
      } else {
        // from が fetchableFrom 以降の場合、 pendings に追加
        removedPendings.push(pending)
      }
    }
  } else {
    // fetchableRange が定義されていない場合、全ての pending を removedPendings に追加
    removedPendings.push(...pendings)
  }

  // pendings , losts をそれぞれ重複・連続しているものを merge する
  const mergedPendings = mergeRanges(removedPendings)
  const mergedLosts = mergeRanges(losts)

  return {
    pendings: mergedPendings,
    losts: mergedLosts,
  }
}

const mergeRanges = (ranges: RangeData[]): RangeData[] => {
  const mergedRanges: RangeData[] = []

  // from で昇順 sort
  ranges.sort((a, b) => a.from - b.from)

  // ranges を順番に処理して、連続している範囲を merge する
  let currentRange: RangeData | undefined
  for (const range of ranges) {
    // currentRange が undefined の場合、range を currentRange に設定
    if (!currentRange) {
      currentRange = { from: range.from, to: range.to }
      continue
    }

    // currentRange と range が連続している場合、currentRange の to を更新
    if (currentRange.to >= range.from - 1) {
      currentRange.to = Math.max(currentRange.to, range.to)
      continue
    }
    // currentRange と range が連続していない場合、currentRange を mergedRanges に追加して currentRange を range に更新
    mergedRanges.push(currentRange)
    currentRange = { from: range.from, to: range.to }
  }
  if (currentRange) {
    mergedRanges.push(currentRange)
  }
  return mergedRanges
}

const saveData = async (params: {
  apiId: number
  from: number
  losts: RangeData[]
  pendings: RangeData[]
  to: number
  transaction: DatabaseWithTransaction['transaction']
}) => {
  const { apiId, from, losts, pendings, to, transaction } = params

  await transaction([
    (tx) => [
      // accountApiLost の既存 apiId 関連データを全削除
      tx
        .delete(accountApiLostTable)
        .where(eq(accountApiLostTable.apiId, apiId)),
      // accountApiPending の既存 apiId 関連データを全削除
      tx
        .delete(accountApiPendingTable)
        .where(eq(accountApiPendingTable.apiId, apiId)),
    ],
    (tx) => [
      // accountApi の from, to を更新
      tx
        .update(accountApiTable)
        .set({
          from: new Date(from),
          to: new Date(to),
        })
        .where(eq(accountApiTable.id, apiId)),
      // accountApiLost に losts を保存
      ...divideArrayIntoChunks(
        losts,
        getMaxInsertRowCount(accountApiLostColumnCount),
      ).map((chunk) =>
        tx
          .insert(accountApiLostTable)
          .values(
            chunk.map((item) => ({
              apiId,
              from: new Date(item.from),
              to: new Date(item.to),
            })),
          )
          .onConflictDoNothing(),
      ),
      // accountApiPending に pendings を保存
      ...divideArrayIntoChunks(
        pendings,
        getMaxInsertRowCount(accountApiPendingColumnCount),
      ).map((chunk) =>
        tx
          .insert(accountApiPendingTable)
          .values(
            chunk.map((item) => ({
              apiId,
              from: new Date(item.from),
              to: new Date(item.to),
            })),
          )
          .onConflictDoNothing(),
      ),
    ],
  ])
}
